import { db, COLLECTIONS } from '../firebase/db';
import { doc, getDoc, setDoc, updateDoc, Timestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { NetworkPolicy, CategoryFilter, DEFAULT_CATEGORIES } from './types';

// Get network policy
export async function getNetworkPolicy(
  familyId: string,
  deviceId: string
): Promise<NetworkPolicy | null> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    const snapshot = await getDoc(policyRef);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    return {
      id: deviceId,
      vpnEnabled: data.vpnEnabled || false,
      vpnPermissionGranted: data.vpnPermissionGranted || false,
      dnsFilteringEnabled: data.dnsFilteringEnabled !== false,
      safeSearchEnabled: data.safeSearchEnabled !== false,
      categoryFiltersEnabled: data.categoryFiltersEnabled !== false,
      blockedDomains: data.blockedDomains || [],
      allowedDomains: data.allowedDomains || [],
      categoryFilters: data.categoryFilters || DEFAULT_CATEGORIES,
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined,
    };
  } catch (err) {
    console.error('Failed to load network policy:', err);
    return null;
  }
}

// Update network policy (partial)
export async function updateNetworkPolicy(
  familyId: string,
  deviceId: string,
  policy: Partial<NetworkPolicy>
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');

    const { id: _id, ...rest } = policy as NetworkPolicy & { id?: string };
    const updateData: Record<string, unknown> = {
      ...rest,
      updatedAt: Timestamp.now(),
    };

    await setDoc(policyRef, updateData, { merge: true });
  } catch (err) {
    throw new Error(`Failed to update network policy: ${(err as Error).message}`);
  }
}

// Add domain to blocked list
export async function addBlockedDomain(
  familyId: string,
  deviceId: string,
  domain: string
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    await updateDoc(policyRef, {
      blockedDomains: arrayUnion(domain),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    throw new Error(`Failed to add blocked domain: ${(err as Error).message}`);
  }
}

// Remove domain from blocked list
export async function removeBlockedDomain(
  familyId: string,
  deviceId: string,
  domain: string
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    await updateDoc(policyRef, {
      blockedDomains: arrayRemove(domain),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    throw new Error(`Failed to remove blocked domain: ${(err as Error).message}`);
  }
}

// Add domain to allowed list
export async function addAllowedDomain(
  familyId: string,
  deviceId: string,
  domain: string
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    await updateDoc(policyRef, {
      allowedDomains: arrayUnion(domain),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    throw new Error(`Failed to add allowed domain: ${(err as Error).message}`);
  }
}

// Remove domain from allowed list
export async function removeAllowedDomain(
  familyId: string,
  deviceId: string,
  domain: string
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    await updateDoc(policyRef, {
      allowedDomains: arrayRemove(domain),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    throw new Error(`Failed to remove allowed domain: ${(err as Error).message}`);
  }
}

// Update category filter
export async function updateCategoryFilter(
  familyId: string,
  deviceId: string,
  categoryId: string,
  enabled: boolean
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');
    const policy = await getNetworkPolicy(familyId, deviceId);

    if (!policy) {
      throw new Error('Network policy not found');
    }

    const updatedCategories = policy.categoryFilters.map(cat =>
      cat.id === categoryId ? { ...cat, enabled } : cat
    );

    await updateDoc(policyRef, {
      categoryFilters: updatedCategories,
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    throw new Error(`Failed to update category filter: ${(err as Error).message}`);
  }
}

// Reset network policy to defaults
export async function resetNetworkPolicy(
  familyId: string,
  deviceId: string
): Promise<void> {
  try {
    const policyRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'network', 'policy');

    const defaults: Omit<NetworkPolicy, 'id'> & { updatedAt: ReturnType<typeof Timestamp.now> } = {
      vpnEnabled: false,
      vpnPermissionGranted: false,
      dnsFilteringEnabled: true,
      safeSearchEnabled: true,
      categoryFiltersEnabled: true,
      blockedDomains: [],
      allowedDomains: [],
      categoryFilters: DEFAULT_CATEGORIES,
      updatedAt: Timestamp.now(),
    };

    await setDoc(policyRef, defaults, { merge: true });
  } catch (err) {
    throw new Error(`Failed to reset network policy: ${(err as Error).message}`);
  }
}
